<template>
  <v-dialog
    :model-value="isVisible"
    :width="dialogWidth"
    :fullscreen="smAndDown"
    @update:model-value="$emit('update:isVisible', $event)"
  >
    <v-card class="d-flex flex-column">
      <v-card-title class="pa-0">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>{{ $t('labels.edit-image') }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              @click="$emit('update:isVisible', false)"
            >
              <v-icon size="large">mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>

      <v-card-text class="pa-4 cropper">
        <!-- Aspect Ratio Controls -->
        <div
          v-if="!hideAspectRatioControls && !processing"
          class="mb-4"
        >
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedAspectRatio"
                :items="aspectRatioOptions"
                :label="$t('labels.aspect-ratio')"
                variant="outlined"
                density="compact"
                @update:model-value="updateCropRatio"
              />
            </v-col>
          </v-row>
        </div>
        <div
          v-if="processing"
          class="d-flex flex-column justify-center align-center text-h5 text-center"
        >
          <v-progress-circular
            :size="150"
            :width="10"
            indeterminate
            color="primary"
            class="ma-12"
          />
        </div>

        <VueCropper
          v-if="editFile && !processing"
          ref="cropperRef"
          :src="editFile"
          :aspect-ratio="currentCropRatio"
          :auto-crop-area="autoCropArea"
          :zoomable="true"
          :scalable="true"
          :rotatable="false"
          :checkCrossOrigin="false"
          :checkOrientation="false"
          :modal="true"
          :guides="true"
          :center="true"
          :highlight="true"
          :background="true"
          :responsive="true"
          :restore="true"
          :alt="$t('labels.image-upload.selected-image')"
          @ready="$emit('cropper-ready')"
          @zoom="$emit('cropper-interaction')"
          @cropstart="$emit('cropper-interaction')"
        />

        <div
          v-if="!processing && editFile"
          class="pt-4"
        >
          <v-alert
            v-if="hasErrors"
            type="error"
          >
            <span v-if="isImageToSmall">
              {{ $t('alerts.image-upload.error-too-small', { minWidth, minHeight }) }}<br />
            </span>
            <span v-if="isImageToBig">
              {{ $t('alerts.image-upload.error-too-big', { maxWidth, maxHeight }) }}<br />
            </span>
            <span v-if="isImageFileToBig">
              {{ $t('alerts.image-upload.error-file', { max: maxImageFileSize / (1024 * 1024) })
              }}<br />
            </span>
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions class="justify-space-between">
        <v-btn
          variant="text"
          @click="$emit('update:isVisible', false)"
        >
          {{ $t('labels.cancel') }}
        </v-btn>
        <v-btn
          variant="text"
          color="primary"
          :disabled="hasErrors || !editFile || processing"
          :loading="isSaving"
          @click="$emit('save')"
        >
          {{ $t('labels.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import 'cropperjs/dist/cropper.css'

import { computed, ref, watch } from 'vue'
import type { VueCropperMethods } from 'vue-cropperjs'
import VueCropper from 'vue-cropperjs'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

interface Props {
  isVisible: boolean
  editFile: string
  processing: boolean
  isSaving: boolean
  dialogWidth?: number
  cropRatio?: number
  autoCropArea: number
  hasErrors: boolean
  isImageToSmall: boolean
  isImageToBig: boolean
  isImageFileToBig: boolean
  minWidth: number
  minHeight: number
  maxWidth: number
  maxHeight: number
  maxImageFileSize: number
  hideAspectRatioControls?: boolean
}

interface Emits {
  'update:isVisible': [value: boolean]
  save: []
  'cropper-ready': []
  'aspect-ratio-changed': [ratio: number]
  'cropper-interaction': []
}

const props = withDefaults(defineProps<Props>(), {
  hideAspectRatioControls: false,
  dialogWidth: 600,
})

const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const { t: _t } = useI18n() // Used in templates

// Types for better DX
type AspectRatios = 'free' | '4:3' | '16:9' | '1:1' | '3:2' | '4:5'

// Cropper ref
const cropperRef = ref<VueCropperMethods | null>(null)

// Aspect ratio state — 'free' means no constraint, crop box starts at full image
const selectedAspectRatio = ref<AspectRatios>('free')

// Aspect ratio options
const aspectRatioOptions = [
  { title: 'Free', value: 'free' },
  { title: '4:3', value: '4:3' },
  { title: '16:9', value: '16:9' },
  { title: '1:1', value: '1:1' },
  { title: '3:2', value: '3:2' },
  { title: '4:5', value: '4:5' },
]

// Computed crop ratio based on selection - NaN = free-form (no constraint)
const currentCropRatio = computed(() => {
  if (selectedAspectRatio.value === 'free') return NaN

  const ratio = selectedAspectRatio.value.split(':').map(Number)

  // Special handling for 4:3 to make it portrait (3:4)
  if (selectedAspectRatio.value === '4:3') {
    return ratio[1] / ratio[0]
  }

  return ratio[0] / ratio[1]
})

// Watch for crop ratio changes and emit to parent
watch(currentCropRatio, (newRatio) => {
  emit('aspect-ratio-changed', newRatio)
})

// Method to update crop ratio
const updateCropRatio = () => {
  const newRatio = currentCropRatio.value
  emit('aspect-ratio-changed', newRatio)
}

// Initialize default values based on props
watch(
  () => props.cropRatio,
  (newRatio) => {
    if (typeof newRatio !== 'number' || isNaN(newRatio)) {
      selectedAspectRatio.value = 'free'
      return
    }
    // Try to match the incoming ratio to our presets
    const tolerance = 0.01
    if (Math.abs(newRatio - 4 / 3) < tolerance) {
      selectedAspectRatio.value = '4:3'
    } else if (Math.abs(newRatio - 16 / 9) < tolerance) {
      selectedAspectRatio.value = '16:9'
    } else if (Math.abs(newRatio - 1) < tolerance) {
      selectedAspectRatio.value = '1:1'
    } else if (Math.abs(newRatio - 3 / 2) < tolerance) {
      selectedAspectRatio.value = '3:2'
    } else if (Math.abs(newRatio - 4 / 5) < tolerance) {
      selectedAspectRatio.value = '4:5'
    } else if (Math.abs(newRatio - 3 / 4) < tolerance) {
      // Special case for Facebook Ads (3:4)
      selectedAspectRatio.value = '4:3' // We'll invert it in currentCropRatio
    }
  },
  { immediate: true }
)

// Expose cropper ref to parent
defineExpose({
  cropperRef,
})
</script>

<style scoped>
.cropper > div {
  max-height: 60vh;
}

/* Remove the problematic deep selectors that are causing the 1px height issue */

.cropper {
  flex-grow: 0 !important;
}
@media (min-width: 960px) {
  .cropper {
    flex-grow: 1 !important;
  }
}

:deep(.cropper-face) {
  background-color: transparent !important;
}
</style>
